import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Mic2, Video, Radio, Camera, Headphones, Building2, Search, ExternalLink, Users } from 'lucide-react';
import { StageEmptyState } from '../components/StageEmptyState';
import PeopleYouMayKnow from '../components/PeopleYouMayKnow';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';



// CreativeCard component - moved outside to prevent re-renders
const CreativeCard = ({ item, type, nameKey, linkPrefix, icon: Icon, accentColor }) => (
  <Link to={`${linkPrefix}/${item[`${type}_id`]}`} data-testid={`${type}-card-${item[`${type}_id`]}`}>
    <Card className="glass-card border-border hover:border-border transition-all group h-full">
      <div className={`h-2 bg-gradient-to-r ${accentColor}`} />
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-full bg-muted/30 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {item.avatar_url ? (
              <img src={item.avatar_url} alt={item[nameKey]} className="w-full h-full object-cover" />
            ) : (
              <Icon className="w-6 h-6 text-muted-foreground" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold group-hover:text-secondary transition-colors truncate">
              {item[nameKey]}
            </h3>
            {item.category && (
              <Badge variant="outline" className="text-xs mt-1">
                {item.category}
              </Badge>
            )}
            {item.stream_type && (
              <Badge variant="outline" className="text-xs mt-1">
                {item.stream_type}
              </Badge>
            )}
            {item.specialties && item.specialties.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {item.specialties.slice(0, 2).map((s, i) => (
                  <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
                ))}
              </div>
            )}
          </div>
        </div>
        {item.bio && (
          <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{item.bio}</p>
        )}
        <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
          {item.episodes_count !== undefined && (
            <span>{item.episodes_count} episodes</span>
          )}
          {item.interviews_count !== undefined && (
            <span>{item.interviews_count} interviews</span>
          )}
          {item.credits_count !== undefined && (
            <span>{item.credits_count} credits</span>
          )}
          {item.subscribers_count !== undefined && (
            <span>{item.subscribers_count} subscribers</span>
          )}
        </div>
      </div>
    </Card>
  </Link>
);

export default function CreativesPage() {
  const [podcasters, setPodcasters] = useState([]);
  const [streamers, setStreamers] = useState([]);
  const [videographers, setVideographers] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('podcasters');

  useEffect(() => {
    fetchAllCreatives();
  }, []);

  const fetchAllCreatives = async () => {
    try {
      const [podRes, streamRes, vidRes, engRes] = await Promise.all([
        fetch(`${API}/podcasters`),
        fetch(`${API}/streamers`),
        fetch(`${API}/videographers`),
        fetch(`${API}/engineers`)
      ]);
      
      if (podRes.ok) setPodcasters(await podRes.json());
      if (streamRes.ok) setStreamers(await streamRes.json());
      if (vidRes.ok) setVideographers(await vidRes.json());
      if (engRes.ok) setEngineers(await engRes.json());
    } catch (error) {
      console.error('Failed to fetch creatives:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterItems = (items, nameKey) => {
    if (!searchQuery) return items;
    return items.filter(item => 
      item[nameKey]?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.bio?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totalCreatives = podcasters.length + streamers.length + videographers.length + engineers.length;

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="creatives-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase mb-2">
            DMV <span className="text-secondary">Creatives</span>
          </h1>
          <p className="text-muted-foreground text-lg">
            Podcasters, hosts, videographers, and engineers powering the DMV scene
          </p>
        </div>

        {/* People You May Know */}
        <div className="mb-8">
          <PeopleYouMayKnow variant="full" limit={12} />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <Card className="glass-card border-green-500/30 p-4 text-center">
            <Mic2 className="w-6 h-6 mx-auto mb-2 text-green-400" />
            <p className="text-2xl font-bold">{podcasters.length}</p>
            <p className="text-xs text-muted-foreground">Podcasters</p>
          </Card>
          <Card className="glass-card border-red-500/30 p-4 text-center">
            <Radio className="w-6 h-6 mx-auto mb-2 text-red-400" />
            <p className="text-2xl font-bold">{streamers.length}</p>
            <p className="text-xs text-muted-foreground">Streamers/Hosts</p>
          </Card>
          <Card className="glass-card border-purple-500/30 p-4 text-center">
            <Camera className="w-6 h-6 mx-auto mb-2 text-purple-400" />
            <p className="text-2xl font-bold">{videographers.length}</p>
            <p className="text-xs text-muted-foreground">Videographers</p>
          </Card>
          <Card className="glass-card border-blue-500/30 p-4 text-center">
            <Headphones className="w-6 h-6 mx-auto mb-2 text-blue-400" />
            <p className="text-2xl font-bold">{engineers.length}</p>
            <p className="text-xs text-muted-foreground">Engineers</p>
          </Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search creatives..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-muted/50 border-border"
            data-testid="creatives-search"
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-muted/50 mb-6 flex-wrap">
            <TabsTrigger value="podcasters" className="flex items-center gap-2">
              <Mic2 className="w-4 h-4" />
              Podcasters ({podcasters.length})
            </TabsTrigger>
            <TabsTrigger value="streamers" className="flex items-center gap-2">
              <Radio className="w-4 h-4" />
              Streamers ({streamers.length})
            </TabsTrigger>
            <TabsTrigger value="videographers" className="flex items-center gap-2">
              <Camera className="w-4 h-4" />
              Videographers ({videographers.length})
            </TabsTrigger>
            <TabsTrigger value="engineers" className="flex items-center gap-2">
              <Headphones className="w-4 h-4" />
              Engineers ({engineers.length})
            </TabsTrigger>
          </TabsList>

          {/* Podcasters Tab */}
          <TabsContent value="podcasters">
            {filterItems(podcasters, 'show_name').length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filterItems(podcasters, 'show_name').map((pod) => (
                  <CreativeCard
                    key={pod.podcaster_id}
                    item={pod}
                    type="podcaster"
                    nameKey="show_name"
                    linkPrefix="/podcasters"
                    icon={Mic2}
                    accentColor="from-green-500 to-emerald-500"
                  />
                ))}
              </div>
            ) : (
              <EmptyState icon={Mic2} label="podcasters" />
            )}
          </TabsContent>

          {/* Streamers Tab */}
          <TabsContent value="streamers">
            {filterItems(streamers, 'display_name').length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filterItems(streamers, 'display_name').map((streamer) => (
                  <CreativeCard
                    key={streamer.streamer_id}
                    item={streamer}
                    type="streamer"
                    nameKey="display_name"
                    linkPrefix="/streamers"
                    icon={Radio}
                    accentColor="from-red-500 to-orange-500"
                  />
                ))}
              </div>
            ) : (
              <EmptyState icon={Radio} label="streamers" />
            )}
          </TabsContent>

          {/* Videographers Tab */}
          <TabsContent value="videographers">
            {filterItems(videographers, 'display_name').length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filterItems(videographers, 'display_name').map((vid) => (
                  <CreativeCard
                    key={vid.videographer_id}
                    item={vid}
                    type="videographer"
                    nameKey="display_name"
                    linkPrefix="/videographers"
                    icon={Camera}
                    accentColor="from-purple-500 to-pink-500"
                  />
                ))}
              </div>
            ) : (
              <EmptyState icon={Camera} label="videographers" />
            )}
          </TabsContent>

          {/* Engineers Tab */}
          <TabsContent value="engineers">
            {filterItems(engineers, 'display_name').length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filterItems(engineers, 'display_name').map((eng) => (
                  <CreativeCard
                    key={eng.engineer_id}
                    item={eng}
                    type="engineer"
                    nameKey="display_name"
                    linkPrefix="/engineers"
                    icon={Headphones}
                    accentColor="from-blue-500 to-cyan-500"
                  />
                ))}
              </div>
            ) : (
              <EmptyState icon={Headphones} label="engineers" />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, label }) {
  const variantMap = {
    podcasters: 'podcasters',
    streamers: 'creatives',
    videographers: 'creatives',
    engineers: 'creatives'
  };
  
  return (
    <StageEmptyState 
      variant={variantMap[label] || 'creatives'}
      customHeadline={`No ${label} yet.`}
      customHint={`Be the first ${label.slice(0, -1)} to join DMV Soundstage.`}
    />
  );
}
